import type { Dispatch, SetStateAction } from 'react';
import L from 'leaflet';
import type { GridParams } from 'contexts/GridContext';
import { lv95ToWgs84ForOneCoordinate, wgs84ToLv95ForOneCoordinate } from '../map/coordinateTransforms';
import { buildTurfPolygonFromLatLngs } from './cadastralGeometry';
import {
  getLayerOuterRing,
  layerRingToLv95,
  type BorderType,
  type Lv95Coordinate,
  type TurfPolygon,
} from './cadastralShared';

interface CadastralTransformHandlerOptions {
  legalBorderRef: { current: L.Polygon | null };
  buildableBorderRef: { current: L.Polygon | null };
  analysisAreaRef: { current: L.Polygon | null };
  activeAnalysisAreaIndex: number;
  analysisAreas: Lv95Coordinate[][];
  gridParams: GridParams;
  getBorderCoordinatesByType: (borderType: BorderType) => Lv95Coordinate[];
  validateProjectRadiusRequirement: (borderType: BorderType, polygon: TurfPolygon | null) => boolean;
  validateAnalysisAreaSeparation: (
    borderType: BorderType,
    polygon: TurfPolygon | null,
    options: { ignoreIndex?: number }
  ) => boolean;
  syncActiveAnalysisAreaCoordinates: (
    coordinates: Lv95Coordinate[],
    options: { analysisAreaType?: string; recenter?: boolean }
  ) => void;
  syncNonAnalysisBorder: (borderType: Exclude<BorderType, 'analysisArea'>, coordinates: Lv95Coordinate[]) => void;
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
}

const createCadastralTransformHandlers = ({
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  activeAnalysisAreaIndex,
  analysisAreas,
  gridParams,
  getBorderCoordinatesByType,
  validateProjectRadiusRequirement,
  validateAnalysisAreaSeparation,
  syncActiveAnalysisAreaCoordinates,
  syncNonAnalysisBorder,
  setCoordinates,
}: CadastralTransformHandlerOptions) => {
  const getLayer = (borderType: BorderType) => {
    switch (borderType) {
      case 'legalBorder':
        return legalBorderRef.current;
      case 'buildableBorder':
        return buildableBorderRef.current;
      case 'analysisArea':
        return analysisAreaRef.current;
      default:
        return null;
    }
  };

  const syncCoordinates = (borderType: BorderType, coordinates: Lv95Coordinate[]) => {
    if (borderType === 'analysisArea') {
      syncActiveAnalysisAreaCoordinates(coordinates, {
        analysisAreaType:
          analysisAreas.length > 1 ? 'newAnalysisArea' : gridParams.analysisAreaType || 'newAnalysisArea',
        recenter: true,
      });
    } else {
      syncNonAnalysisBorder(borderType, coordinates);
    }
    setCoordinates(coordinates);
  };

  const restoreCoordinates = (borderType: BorderType, layer: L.Polygon) => {
    const coordinates = getBorderCoordinatesByType(borderType).map((coordinate) =>
      lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y)
    );
    layer.setLatLngs(coordinates);
  };

  const isTransformValid = (borderType: BorderType, layer: L.Polygon) => {
    const polygon = buildTurfPolygonFromLatLngs(getLayerOuterRing(layer));
    return (
      validateProjectRadiusRequirement(borderType, polygon) &&
      validateAnalysisAreaSeparation(borderType, polygon, { ignoreIndex: activeAnalysisAreaIndex })
    );
  };

  const enableRotateMode = (borderType: BorderType) => {
    const layer = getLayer(borderType);
    if (!layer) return;

    layer.pm.enableRotate();
    layer.on('pm:rotateend', (event) => {
      const editedLayer = event.layer as L.Polygon;
      if (!isTransformValid(borderType, editedLayer)) {
        restoreCoordinates(borderType, editedLayer);
        editedLayer.pm?.disableRotate?.();
        return;
      }
      syncCoordinates(
        borderType,
        getLayerOuterRing(editedLayer).map((coordinate) => wgs84ToLv95ForOneCoordinate(coordinate.lng, coordinate.lat))
      );
    });
  };

  const enableDragMode = (borderType: BorderType) => {
    const layer = getLayer(borderType);
    if (!layer) return;

    layer.pm.enableLayerDrag();
    layer.on('pm:dragend', (event) => {
      const editedLayer = event.layer as L.Polygon;
      if (!isTransformValid(borderType, editedLayer)) {
        restoreCoordinates(borderType, editedLayer);
        editedLayer.pm?.disableLayerDrag?.();
        return;
      }
      syncCoordinates(borderType, layerRingToLv95(event.layer));
    });
  };

  return { enableRotateMode, enableDragMode };
};

export default createCadastralTransformHandlers;
